import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Typography,
  tokens,
  TextField,
  Textarea,
  Select,
  MultiSelect,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import type { DesignPreferenceItemFormatted } from "../../types/designPreferences";
import { getDisplayValue } from "../../utils/groupDesignPreferences";
import {
  sanitizeTextInput,
  sanitizePhoneInput,
  MAX_TEXT_INPUT_LENGTH,
  MAX_PHONE_LENGTH,
  MAX_TEXTAREA_LENGTH,
} from "../../utils/validateTextInput";
import { RadioButtonGroup } from "./RadioButtonGroup";
import { CheckboxGroup } from "./CheckboxGroup";
import { DesignPreferenceOutputText } from "./DesignPreferenceOutputText";
import { ViewMode } from "checkout/types/payments-ui-form";

const SCOPE_OF_WORK_QUESTION =
  "If Scope of Work is required on the project, which type (if able) should be customer managed?";
const SUNRUN_MANAGED = "Sunrun Managed";

type MultiSelectOption = {
  id: string;
  text: string;
  checked: boolean;
  disabled?: boolean;
};

type DesignPreferenceFieldProps = {
  item: DesignPreferenceItemFormatted;
  viewMode: ViewMode;
  value: string;
  onChange: (value: string) => void;
  fieldIndex: number;
  renderAsHyperlink?: boolean;
  showRequiredAsterisk?: boolean;
};

const DesignPreferenceField: React.FC<DesignPreferenceFieldProps> = ({
  item,
  viewMode,
  value,
  onChange,
  fieldIndex,
  renderAsHyperlink = false,
  showRequiredAsterisk = false,
}) => {
  const handleTextChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    maxLength: number,
    textArea: boolean = false
  ) => {
    const sanitized = sanitizeTextInput(e.target.value, maxLength, textArea);
    onChange(sanitized);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizePhoneInput(e.target.value);
    onChange(sanitized);
  };

  // the scope of work question is a special case where the "sunrun managed" option is disabled if any other option is selected and vis versa
  const isScopeOfWorkQuestion = item.question === SCOPE_OF_WORK_QUESTION;
  const applyScopeOfWorkDisabled = useCallback(
    (opts: MultiSelectOption[], selectedValues: string[]) => {
      if (!isScopeOfWorkQuestion) {
        return opts.map((o) => ({ ...o, disabled: false }));
      }

      const sunrunManagedSelected = selectedValues.includes(SUNRUN_MANAGED);
      const otherOptionsSelected = selectedValues.some(
        (v) => v !== SUNRUN_MANAGED
      );

      return opts.map((opt) => {
        const isSunrunManagedOption = opt.id === SUNRUN_MANAGED;
        const disabled = isSunrunManagedOption
          ? otherOptionsSelected
          : sunrunManagedSelected;

        return { ...opt, disabled };
      });
    },
    [isScopeOfWorkQuestion]
  );

  const isMultiSelect = item.respDataType === "MultiSelectPicklist";
  const multiSelectOptionsFromValue = useMemo(() => {
    if (!isMultiSelect) return [];
    const opts = item.responseList ?? [];
    const selectedValues = value
      .split(/[;,]/)
      .map((v) => v.trim())
      .filter((v) => v !== "");
    const baseOptions = opts.map((opt) => ({
      id: opt.value,
      text: opt.value,
      checked: selectedValues.includes(opt.value),
    }));
    return applyScopeOfWorkDisabled(baseOptions, selectedValues);
  }, [isMultiSelect, value, item.responseList, applyScopeOfWorkDisabled]);

  const [multiSelectOptions, setMultiSelectOptions] = useState<
    MultiSelectOption[]
  >([]);

  useEffect(() => {
    if (isMultiSelect) {
      setMultiSelectOptions(multiSelectOptionsFromValue);
    }
  }, [isMultiSelect, multiSelectOptionsFromValue]);

  const onChangeMultiSelect = (updatedOptions: MultiSelectOption[]) => {
    let selected = updatedOptions
      .filter((opt) => opt.checked)
      .map((opt) => opt.id);
    if (isScopeOfWorkQuestion && selected.includes(SUNRUN_MANAGED)) {
      selected = [SUNRUN_MANAGED];
    }

    const optionsWithCorrectedChecked = updatedOptions.map((o) => ({
      ...o,
      checked: selected.includes(o.id),
    }));
    const optionsWithDisabled = applyScopeOfWorkDisabled(
      optionsWithCorrectedChecked,
      selected
    );
    setMultiSelectOptions(optionsWithDisabled);
    onChange(selected.join(";"));
  };

  if (viewMode === "view") {
    if (
      item.respDataType === "OutputText" &&
      item.question !== "Phone" &&
      item.question !== "Email"
    ) {
      return null;
    }
    const displayValue = getDisplayValue(item);
    return (
      <InfoField>
        <Typography color={tokens.TINTS_OFF_WHITE_20}>
          {item.question}
        </Typography>
        <Typography>{displayValue}</Typography>
      </InfoField>
    );
  }

  const renderEditMode = () => {
    const label = (
      <Typography color={tokens.TINTS_OFF_WHITE_20}>
        {item.question}
        {(item.isRequired || showRequiredAsterisk) && (
          <RequiredIndicator> *</RequiredIndicator>
        )}
      </Typography>
    );

    switch (item.respDataType) {
      case "Phone":
        return (
          <InfoField>
            {label}
            <TextField
              type="tel"
              value={value}
              onChange={handlePhoneChange}
              maxLength={MAX_PHONE_LENGTH}
            />
          </InfoField>
        );

      case "Text":
        return (
          <InfoField>
            {label}
            <TextField
              value={value}
              onChange={(e) => handleTextChange(e, MAX_TEXT_INPUT_LENGTH)}
              maxLength={MAX_TEXT_INPUT_LENGTH}
            />
          </InfoField>
        );

      case "Text Area":
        return (
          <InfoField>
            {label}
            <Textarea
              value={value}
              onChange={(e) => handleTextChange(e, MAX_TEXTAREA_LENGTH, true)}
              maxLength={MAX_TEXTAREA_LENGTH}
            />
          </InfoField>
        );

      case "Radio Button": {
        const options = item.responseList ?? [];

        const questionLower = item.question.toLowerCase();
        const isCheckboxField =
          questionLower.includes("best way to reach") ||
          questionLower.includes("best hours to reach");

        if (isCheckboxField) {
          const selectedValues = value
            ? value
                .split(",")
                .map((v) => v.trim())
                .filter((v) => v !== "")
            : [];

          return (
            <InfoField>
              {label}
              <CheckboxGroup
                options={options}
                values={selectedValues}
                onChange={(values) => onChange(values.join(", "))}
                layout="row"
                name={`checkbox-${fieldIndex}`}
              />
            </InfoField>
          );
        }

        return (
          <InfoField>
            {label}
            <RadioButtonGroup
              options={options}
              value={value}
              onChange={onChange}
              layout="column"
              name={`radio-${fieldIndex}`}
            />
          </InfoField>
        );
      }

      case "Picklist": {
        const options = item.responseList ?? [];
        const selectOptions = options.map((opt) => ({
          label: opt.label,
          value: opt.value,
        }));

        return (
          <InfoField>
            {label}
            <Select
              value={value || selectOptions[0]?.value || ""}
              options={selectOptions}
              onChange={({ target }) => onChange(target.value)}
              testId={`select-${fieldIndex}`}
            />
          </InfoField>
        );
      }

      case "MultiSelectPicklist": {
        const optionsToShow =
          multiSelectOptions.length > 0
            ? multiSelectOptions
            : multiSelectOptionsFromValue;
        return (
          <InfoField>
            {label}
            <StyledMultiSelect
              className="multi-select"
              cols={optionsToShow.length}
              options={optionsToShow}
              onChange={onChangeMultiSelect}
              testId={`select-${fieldIndex}`}
            />
          </InfoField>
        );
      }

      case "OutputText":
        return (
          <DesignPreferenceOutputText
            item={item}
            renderAsHyperlink={renderAsHyperlink}
          />
        );

      default: {
        const fallbackValue = getDisplayValue(item);
        return (
          <InfoField>
            {label}
            <Typography>{fallbackValue}</Typography>
          </InfoField>
        );
      }
    }
  };

  return renderEditMode();
};

const InfoField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;

  & > * {
    overflow-wrap: break-word;
    word-break: break-word;
  }
`;

const RequiredIndicator = styled.span`
  color: ${tokens.ERROR_50};
`;

const StyledMultiSelect = styled(MultiSelect)<{ cols: number }>`
  grid-template-columns: repeat(
    ${({ cols }) => cols},
    minmax(min-content, max-content)
  ) !important;
`;

export { DesignPreferenceField };
